import { useState } from 'react';
import { faAdd } from '@fortawesome/pro-regular-svg-icons';
import { Chip, IconButton, Stack, Tooltip } from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';

import { CreateModelModal } from '../Modal/Model/CreateModelModal';
import { UpdateModelModal } from '../Modal/Model/UpdateModelModal';
import { DeleteModelModal } from '../Modal/Model/DeleteModelModal';
import { ReactivateModelModal } from '../Modal/Model/ReactivateModelModal';
import { GlobalVariables } from '../../globalVar';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { useTableLocale } from '../../hooks/useTableLocale';
import { useTranslationHook } from '../../hooks/UseTranslationHook';
import usePersistedColumnOrder from '../../hooks/UseCustomColumnOrder';

const LOCAL_COLUMN_WIDTH = GlobalVariables.getDefaultColumnWidth();

export const ModelTable = ({ data, isFetching, fetchData }) => {
  const [modelId, setModelId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const {
    addString,
    editString,
    deactivateString,
    reactivateString,
    activeString,
    inactiveString,
    createdAtString,
    createdByString,
    updatedAtString,
    updatedByString,
  } = useTranslationHook();
  const locale = useTableLocale();

  const initialColumns = [
    {
      field: 'id',
      headerName: 'ID',
      headerAlign: 'center',
      align: 'center',
      type: 'number',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
    },
    {
      field: 'name',
      headerName: 'Nombre',
      type: 'string',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
    },
    { field: 'is_active', headerName: 'Estatus', width: 150 },
    { field: 'brand_name', headerName: 'Marca', width: 150 },
    { field: 'created_at', headerName: createdAtString, width: 150 },
    { field: 'created_by_name', headerName: createdByString, width: 150 },
    { field: 'updated_at', headerName: updatedAtString, width: 150 },
    { field: 'updated_by_name', headerName: updatedByString, width: 150 },
    // { field: 'notes', headerName: 'Notas', width: 200 },
  ].map((column) => ({
    ...column,
    renderCell: (params) =>
      ['created_at', 'updated_at'].includes(column.field)
        ? formatDateDDMMYYYY(params.row[column.field])
        : params.row[column.field] || '-',
  }));

  const actionColumn = [
    {
      field: 'action',
      headerName: 'Detalle',
      width: 160,
      renderCell: (params) => {
        if (Number(params.row.is_active) !== 1) {
          return (
            <div
              className="cellAction equips-datagrid-actions-tour"
              style={{
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Tooltip title={reactivateString} arrow>
                <IconButton
                  style={{ background: 'none', border: 'none', padding: 0 }}
                  onClick={() => {
                    setModelId(params.row.id);
                    setShowReactivateModal(true);
                  }}
                >
                  <RestoreIcon
                    className="detail-icons"
                    id="reactivate-icon"
                    style={{ color: 'green' }}
                  />
                </IconButton>
              </Tooltip>
            </div>
          );
        }

        return (
          <div
            className="cellAction equips-datagrid-actions-tour"
            style={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Tooltip title={editString} arrow>
              <IconButton
                style={{ background: 'none', border: 'none', padding: 0 }}
                onClick={() => {
                  setModelId(params.row.id);
                  setShowUpdateModal(true);
                }}
              >
                <ModeEditOutlineOutlinedIcon
                  className="detail-icons"
                  id="update-icon"
                />
              </IconButton>
            </Tooltip>
            <Tooltip title={deactivateString} arrow>
              <IconButton
                style={{ background: 'none', border: 'none', padding: 0 }}
                onClick={() => {
                  setModelId(params.row.id);
                  setShowDeleteModal(true);
                }}
              >
                <BlockIcon
                  className="detail-icons"
                  id="deactivate-icon"
                  style={{ color: 'red' }}
                />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const [columns, updateColumnOrder] = usePersistedColumnOrder(
    'modelTable',
    initialColumns,
  );
  const handleColumnOrderChange = ({ targetIndex, oldIndex }) => {
    const updatedColumns = [...columns];
    const [movedColumn] = updatedColumns.splice(oldIndex, 1);
    updatedColumns.splice(targetIndex, 0, movedColumn);
    updateColumnOrder(updatedColumns);
  };
  const selectedModel = data.find((item) => item.id === modelId);

  const columnsWithStatus = columns.map((column) =>
    column.field === 'is_active'
      ? {
          ...column,
          renderCell: (params) => {
            const isActive = Number(params.row.is_active) === 1;

            return (
              <Chip
                label={isActive ? activeString : inactiveString}
                color={isActive ? 'success' : 'error'}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 'bold', minWidth: 120 }}
              />
            );
          },
        }
      : column,
  );

  return (
    <>
      <div className="datatable-btns-container">
        <div className="dt-btn-main-container">
          <span className="dabiWebText">Modelos</span>
          <button
            className="add-button create-equip-tour"
            onClick={() => setShowCreateModal(true)}
          >
            {addString} <FontAwesomeIcon icon={faAdd} />
          </button>
        </div>
      </div>
      <CreateModelModal
        showModal={showCreateModal}
        setShowModal={setShowCreateModal}
        refreshData={fetchData}
      />
      <UpdateModelModal
        modelId={modelId}
        showModal={showUpdateModal}
        setShowModal={setShowUpdateModal}
        refreshData={fetchData}
      />
      <DeleteModelModal
        modelId={modelId}
        model={selectedModel}
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        refreshData={fetchData}
      />
      <ReactivateModelModal
        modelId={modelId}
        model={selectedModel}
        showModal={showReactivateModal}
        setShowModal={setShowReactivateModal}
        refreshData={fetchData}
      />
      <DataGridPro
        localeText={locale.components.MuiDataGrid.defaultProps.localeText}
        rows={data}
        columns={columnsWithStatus.concat(actionColumn)}
        loading={isFetching}
        onColumnOrderChange={handleColumnOrderChange}
        initialState={{
          sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
          pinnedColumns: { right: ['action'] },
        }}
        slots={{
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              No hay modelos en la base de datos
            </Stack>
          ),
          toolbar: GridToolbar,
        }}
      />
    </>
  );
};
